import Seo from '@/components/Seo';
import SiteHeader from '@/components/site/SiteHeader';
import SiteFooter from '@/components/site/SiteFooter';
import Hover from '@/components/site/Hover';
import { css } from '@/components/site/css';
import { CONTACT_MAILTO, CALENDAR_URL, START_FREE_URL } from '@/config/links';

const SIGNALS: [string, string, string][] = [
  [
    '01',
    'Menu composition',
    "What's currently on the menu: category presence, program depth, and whether there's a logical slot for your brand.",
  ],
  [
    '02',
    'Price positioning',
    'Average menu price tells us whether the venue’s clientele can afford your product. The single most direct filter for brand fit.',
  ],
  [
    '03',
    'Competitor presence',
    'Which competing brands are listed, how many placements they hold, and where the openings are for your brand.',
  ],
  [
    '04',
    'Brand fit',
    'Venues matched to your brand’s style and positioning, so recommendations are conceptually on-brand, not just geographically close.',
  ],
  [
    '05',
    'Geography',
    'High-activity nightlife districts and metro areas where placements are most likely.',
  ],
  [
    '06',
    'Existing placements',
    "Where you're already on menus, so we surface expansion opportunities, not accounts you already have.",
  ],
];

const STEPS: [string, string, string][] = [
  [
    '1',
    'Brief your markets',
    'Tell us which markets to focus on, your target venue types, and your brand positioning. Share your current account list or let us build it over time.',
  ],
  [
    '2',
    'We score every venue',
    'Every relevant venue in your markets is evaluated across the six signals. Each gets a score, a rank, and a reason.',
  ],
  [
    '3',
    'Your list arrives Monday',
    'A scored, prioritized list is ready at the start of every week. A teaser lands in your inbox; the full list and contacts are in our messaging tool.',
  ],
  [
    '4',
    'Hunt, convert, repeat',
    "Your reps walk in warm. Over time, the list gets smarter, informed by which accounts you've visited and where you're already winning.",
  ],
];

const FAQS: [string, string][] = [
  [
    'Where does the data come from?',
    "We analyze on-premise data across your markets: menus, cocktail and spirits programs, pricing, and current placements. You don't need to hand over distributor reports to get started.",
  ],
  [
    'How is a venue scored?',
    'Every venue is ranked across six signals: menu composition, price positioning, competitor presence, brand fit, geography, and existing placements. Each account gets a score, a rank, and the reasoning behind it.',
  ],
  [
    'What lands in my inbox each week?',
    'A teaser with your top accounts arrives at the start of the week. The full scored list, the reasoning, and the named contacts live in our messaging tool, where your reps already work.',
  ],
  [
    'Do you give me a contact at each venue?',
    'Where available, yes: a named decision-maker and their role, such as beverage director, GM, or bar director. Your rep walks in asking for a person by name, not cold.',
  ],
  [
    'What is a seat?',
    'One seat is one member of your field team, and each seat gets 10 scored accounts every week. Add as many seats as you need.',
  ],
  [
    'Does pricing change by state?',
    'No. 10 accounts per seat per week applies to any market. Utah and California cost the same.',
  ],
  ['Is there a contract?', 'No. F&B Insights is month-to-month with no annual commitment.'],
  [
    'How fast can we start?',
    'Days, not weeks. Brief your markets and positioning, share an account list or let us build it over time, and your first list arrives at the start of the next week.',
  ],
  [
    'Can I request custom indicators or deeper analysis?',
    'Yes. Custom indicators, for example Black-owned businesses or venues with three or more rum cocktails, and deeper needs like depletion analysis or custom reporting are available. Book a call with Noah.',
  ],
];

/** Beverage alcohol suppliers page. Ported verbatim from Beverage.dc.html.
 *  "Start free" CTAs point at this site's /start-free (relative). */
export default function BeveragePage() {
  return (
    <div
      className="site-shell"
      style={css(
        "font-family:'Hanken Grotesk',system-ui,sans-serif;color:#24276C;background:#ffffff;-webkit-font-smoothing:antialiased;overflow-x:clip"
      )}
    >
      <Seo
        title="F&B Insights — Find the venues most likely to place your brand"
        description="Every week, a scored list of the venues worth your reps' time, plus the reasoning and the contact to walk in ready. Built for beverage alcohol brands."
        path="/beverage"
      />

      <SiteHeader
        navLinks={[
          { label: 'How it works', href: '#how-it-works' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'FAQ', href: '#faq' },
        ]}
        cta={{ label: 'Start free', to: START_FREE_URL, bg: '#E9402B', hoverBg: '#D3341F' }}
      />

      <span id="top" />

      {/* Hero */}
      <section style={css('background:#24276C')}>
        <div
          className="site-hero-grid"
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px 72px;display:grid;grid-template-columns:minmax(0,1.02fr) minmax(0,0.98fr);gap:56px;align-items:center'
          )}
        >
          <div>
            <div
              style={css(
                'display:inline-flex;align-items:center;gap:9px;font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:22px'
              )}
            >
              <span
                style={css(
                  'width:7px;height:7px;border-radius:50%;background:#E9402B;display:inline-block'
                )}
              ></span>
              For beverage alcohol brands
            </div>
            <h1
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(38px,4.8vw,58px);line-height:1.05;font-weight:800;letter-spacing:-0.02em;margin:0 0 24px;color:#ffffff;text-wrap:balance"
              )}
            >
              Find the venues most likely to place your brand.
            </h1>
            <p
              style={css(
                'font-size:20px;line-height:1.5;color:#B9C0DC;margin:0 0 34px;max-width:520px;text-wrap:pretty'
              )}
            >
              Every week, a scored list of the venues worth your reps&rsquo; time, plus the
              reasoning and the contact to walk in ready.
            </p>
            <div
              className="site-hero-cta"
              style={css('display:flex;flex-wrap:wrap;gap:14px;align-items:center')}
            >
              <Hover
                to={START_FREE_URL}
                style={css(
                  'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('background:#D3341F')}
              >
                Start free<span style={css('font-size:18px;line-height:1')}>→</span>
              </Hover>
              <Hover
                href="#how-it-works"
                style={css(
                  'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#fff;background:transparent;border:1.5px solid rgba(255,255,255,0.35);padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('border-color:#ffffff')}
              >
                See how it works
              </Hover>
            </div>
            <p style={css('font-size:14px;color:#94A0C4;margin:18px 0 0')}>
              No credit card. No setup call. Takes a few minutes.
            </p>
          </div>

          <div style={css('position:relative')}>
            <div
              style={css(
                'position:absolute;inset:-18px -18px auto auto;width:76px;height:76px;border-radius:16px;background:#E9402B;opacity:0.1'
              )}
            ></div>
            <div
              style={css(
                'position:relative;background:#fff;border:1px solid #E6E4DE;border-radius:18px;box-shadow:0 24px 60px -28px rgba(36,39,108,0.32);padding:24px 24px 20px'
              )}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:15px;font-weight:700;color:#24276C"
                  )}
                >
                  Weekly opportunities
                </span>
                <span
                  style={css(
                    'font-size:12px;font-weight:600;color:#54545E;background:#EFEEEA;padding:5px 10px;border-radius:20px'
                  )}
                >
                  Chicago · Week of Jul 7
                </span>
              </div>
              <div style={css('display:flex;flex-direction:column;gap:12px')}>
                <div style={css('border:1px solid #F0EFEB;border-radius:12px;padding:15px 16px')}>
                  <div
                    style={css(
                      'display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px'
                    )}
                  >
                    <span style={css('font-size:15px;font-weight:700;color:#24276C')}>
                      <span style={css('color:#8A8A93')}>1.</span> The Violet Hour
                    </span>
                    <span
                      style={css(
                        'font-size:13px;font-weight:700;color:#12A150;background:#EAF7EF;padding:3px 9px;border-radius:20px'
                      )}
                    >
                      9.6
                    </span>
                  </div>
                  <p style={css('font-size:13px;line-height:1.5;color:#7C7C86;margin:0 0 8px')}>
                    Avg cocktail price $16.50. Robust spirits program: 94 pours, 31 bourbons. No
                    ultra-premium brand in the category. Strong existing market.
                  </p>
                  <div style={css('font-size:13px;font-weight:600;color:#E9402B')}>
                    → Sarah Chen, Beverage Director
                  </div>
                </div>
                <div style={css('border:1px solid #F0EFEB;border-radius:12px;padding:15px 16px')}>
                  <div
                    style={css(
                      'display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px'
                    )}
                  >
                    <span style={css('font-size:15px;font-weight:700;color:#24276C')}>
                      <span style={css('color:#8A8A93')}>2.</span> Bavette&rsquo;s Bar &amp; Boeuf
                    </span>
                    <span
                      style={css(
                        'font-size:13px;font-weight:700;color:#12A150;background:#EAF7EF;padding:3px 9px;border-radius:20px'
                      )}
                    >
                      9.1
                    </span>
                  </div>
                  <p style={css('font-size:13px;line-height:1.5;color:#7C7C86;margin:0 0 8px')}>
                    Avg cocktail price $18.00. Premium steakhouse, 12 cocktail slots. Mid-tier
                    whiskey dominant, no craft bourbon. High-spend clientele.
                  </p>
                  <div style={css('font-size:13px;font-weight:600;color:#E9402B')}>
                    → Marcus Webb, GM
                  </div>
                </div>
              </div>
              <div
                style={css(
                  'text-align:center;margin-top:14px;font-size:13px;font-weight:600;color:#8A8A93'
                )}
              >
                + 8 more accounts this week
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:74px 28px')}>
          <div style={css('max-width:720px;margin-bottom:42px')}>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
              )}
            >
              The problem
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
              )}
            >
              Your reps are hunting, but not always in the right places.
            </h2>
            <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:18px 0 0')}>
              Without a system, account targeting defaults to rep intuition and distributor
              suggestions. Visits spread thin, trade spend scatters, and the best venues get missed.
            </p>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:22px'
            )}
          >
            {[
              [
                'Targeting is instinct-driven',
                'Reps decide which accounts to visit based on familiarity, not probability of conversion.',
              ],
              [
                'Competitor intelligence is missing',
                "You don't know which accounts your competitors are in, how they're performing, or where the openings are for your brand.",
              ],
              [
                'Time spent on low-fit accounts',
                'Field teams cover the same accounts broadly instead of prioritizing the 20–30 most likely to convert.',
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                style={css(
                  'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:28px 26px'
                )}
              >
                <div
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:18px;font-weight:700;color:#24276C;margin-bottom:10px"
                  )}
                >
                  {title}
                </div>
                <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What powers it */}
      <section style={css('max-width:1180px;margin:0 auto;padding:80px 28px')}>
        <div style={css('max-width:720px;margin-bottom:42px')}>
          <div
            style={css(
              'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
            )}
          >
            What powers it
          </div>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
            )}
          >
            Six signals. One ranked list.
          </h2>
          <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:18px 0 0')}>
            Every recommended venue is scored across six inputs, so your reps know not just where to
            go, but why it&rsquo;s worth their time.
          </p>
        </div>
        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:22px'
          )}
        >
          {SIGNALS.map(([num, title, body]) => (
            <div
              key={num}
              style={css(
                'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:28px 26px'
              )}
            >
              <div
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:12px;font-weight:700;color:#E9402B;margin-bottom:14px"
                )}
              >
                {num}
              </div>
              <div
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:18px;font-weight:700;color:#24276C;margin-bottom:9px"
                )}
              >
                {title}
              </div>
              <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
            </div>
          ))}
        </div>
        <p
          style={css(
            'font-size:15px;line-height:1.55;color:#8A8A93;margin:26px 0 0;max-width:760px'
          )}
        >
          Custom indicators available on request, e.g. Black-owned businesses, venues with 3+ rum
          cocktails, specific cocktail program types.
        </p>
      </section>

      {/* Output */}
      <section style={css('background:#24276C;color:#fff')}>
        <div
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px;display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:52px;align-items:center'
          )}
        >
          <div>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:14px'
              )}
            >
              The output
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#fff"
              )}
            >
              Not a dashboard.
              <br />A hunting list.
            </h2>
            <p
              style={css(
                'font-size:18px;line-height:1.55;color:#B9C0DC;margin:0 0 20px;max-width:480px'
              )}
            >
              Delivered every week. Scored, reasoned, and ready to act on. A teaser lands in your
              inbox to pull you in. The full list, the reasoning, and the named contact for each
              account are in our messaging tool, where your reps already are.
            </p>
            <p
              style={css(
                'font-size:18px;line-height:1.55;color:#fff;font-weight:600;margin:0;max-width:480px'
              )}
            >
              You get the who, not just the where.
            </p>
          </div>
          <div
            style={css(
              'background:#1A1C50;border:1px solid #35387E;border-radius:16px;padding:28px'
            )}
          >
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#8B93B8;margin-bottom:18px'
              )}
            >
              Every account includes
            </div>
            <div style={css('display:flex;flex-direction:column;gap:16px')}>
              {[
                ['A score', 'How strong the opportunity is, at a glance.'],
                ['The reasoning', 'Why the venue ranked where it did, in plain terms.'],
                ['A named contact', 'A person to ask for, so your rep walks in warm.'],
              ].map(([title, body]) => (
                <div key={title} style={css('display:flex;gap:14px;align-items:flex-start')}>
                  <span
                    style={css(
                      'flex:none;width:30px;height:30px;border-radius:8px;background:#E9402B;display:flex;align-items:center;justify-content:center;font-weight:700'
                    )}
                  >
                    ✓
                  </span>
                  <div>
                    <div style={css('font-size:16px;font-weight:600;color:#fff')}>{title}</div>
                    <div style={css('font-size:14px;line-height:1.5;color:#9AA2C4')}>{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" style={css('max-width:1180px;margin:0 auto;padding:80px 28px')}>
        <div style={css('max-width:720px;margin-bottom:42px')}>
          <div
            style={css(
              'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
            )}
          >
            How it works
          </div>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
            )}
          >
            Up and running in days.
          </h2>
        </div>
        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:22px'
          )}
        >
          {STEPS.map(([num, title, body]) => (
            <div
              key={num}
              style={css(
                'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:30px 28px'
              )}
            >
              <div
                style={css(
                  "width:44px;height:44px;border-radius:11px;background:#24276C;color:#fff;display:flex;align-items:center;justify-content:center;font-family:'Schibsted Grotesk',sans-serif;font-weight:700;font-size:18px;margin-bottom:20px"
                )}
              >
                {num}
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:19px;font-weight:700;margin:0 0 10px;color:#24276C"
                )}
              >
                {title}
              </h3>
              <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:900px;margin:0 auto;padding:64px 28px;text-align:center')}>
          <div
            style={css(
              'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:16px'
            )}
          >
            In market
          </div>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(24px,2.8vw,34px);line-height:1.14;font-weight:800;letter-spacing:-0.02em;margin:0 0 30px;color:#24276C"
            )}
          >
            Running with emerging and established brands across the US.
          </h2>
          <p
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(20px,2.4vw,26px);line-height:1.32;font-weight:600;color:#24276C;margin:0 auto;max-width:720px;text-wrap:balance"
            )}
          >
            &ldquo;F&amp;B Insights helps us prioritize the accounts most likely to move the needle,
            so we sell smarter and move faster.&rdquo;
          </p>
          <div style={css('margin-top:20px;font-size:15px;font-weight:600;color:#54545E')}>
            Jake Ireland, Founder, Off Hours Bourbon
          </div>
          <div
            style={css(
              'margin-top:30px;font-size:14px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#8A8A93'
            )}
          >
            Coverage in all 50 states
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={css('max-width:1180px;margin:0 auto;padding:80px 28px')}>
        <div style={css('max-width:720px;margin-bottom:42px')}>
          <div
            style={css(
              'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
            )}
          >
            Pricing
          </div>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
            )}
          >
            Simple. Seat-based. State-agnostic.
          </h2>
          <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:18px 0 0')}>
            10 accounts per seat per week, regardless of which state you&rsquo;re in. Your team
            grows, your list grows. That&rsquo;s it. One seat is one member of your field team. Add
            as many as you need.
          </p>
        </div>
        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:22px;align-items:stretch'
          )}
        >
          {/* Free */}
          <div
            style={css(
              'background:#fff;border:1px solid #E6E4DE;border-radius:18px;padding:34px 32px;display:flex;flex-direction:column'
            )}
          >
            <div
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:20px;font-weight:700;color:#24276C"
              )}
            >
              Free
            </div>
            <div
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:38px;font-weight:800;color:#24276C;margin:6px 0 22px"
              )}
            >
              No cost
            </div>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.03em;text-transform:uppercase;color:#8A8A93;margin-bottom:14px'
              )}
            >
              What&rsquo;s included
            </div>
            <div
              style={css(
                'display:flex;gap:10px;align-items:flex-start;font-size:16px;color:#43434D;margin-bottom:26px'
              )}
            >
              <span style={css('color:#12A150;font-weight:700')}>✓</span>5 accounts / week
            </div>
            <Hover
              to={START_FREE_URL}
              style={css(
                'margin-top:auto;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;color:#24276C;background:#fff;border:1.5px solid #D8D6D0;padding:14px;border-radius:10px'
              )}
              hoverStyle={css('border-color:#24276C')}
            >
              Start free →
            </Hover>
          </div>
          {/* Core */}
          <div
            style={css(
              'background:#24276C;border:1px solid #24276C;border-radius:18px;padding:34px 32px;display:flex;flex-direction:column;position:relative;overflow:hidden'
            )}
          >
            <div
              style={css(
                'position:absolute;top:0;right:0;width:120px;height:120px;background:#E9402B;opacity:0.16;border-radius:0 0 0 100%'
              )}
            ></div>
            <div style={css('display:flex;align-items:center;gap:10px;position:relative')}>
              <span
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:20px;font-weight:700;color:#fff"
                )}
              >
                Core
              </span>
              <span
                style={css(
                  'font-size:11px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#fff;background:#E9402B;padding:4px 9px;border-radius:20px'
                )}
              >
                Full access
              </span>
            </div>
            <div style={css('margin:14px 0 22px;position:relative')}>
              {[
                ['Seat 1', '$199/mo'],
                ['Seats 2–5', '$149/mo'],
                ['Seats 6–10', '$99/mo'],
              ].map(([label, price], i) => (
                <div
                  key={label}
                  style={css(
                    `display:flex;justify-content:space-between;font-size:15px;color:#EAECF6;padding:6px 0${i < 2 ? ';border-bottom:1px solid #35387E' : ''}`
                  )}
                >
                  <span>{label}</span>
                  <span style={css('font-weight:700;color:#fff')}>{price}</span>
                </div>
              ))}
            </div>
            <div
              style={css(
                'display:flex;flex-direction:column;gap:11px;margin-bottom:26px;position:relative'
              )}
            >
              {[
                '10 accounts/week per seat',
                'Score + full reasoning per account',
                'Named contacts where available',
                'Email delivery + messaging tool',
                'Any number of seats',
              ].map((f) => (
                <div
                  key={f}
                  style={css(
                    'display:flex;gap:10px;align-items:flex-start;font-size:15px;color:#EAECF6'
                  )}
                >
                  <span style={css('color:#F0806F;font-weight:700')}>✓</span>
                  {f}
                </div>
              ))}
            </div>
            <Hover
              href={CALENDAR_URL}
              style={css(
                'margin-top:auto;display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:14px;border-radius:10px;position:relative'
              )}
              hoverStyle={css('background:#D3341F')}
            >
              Book a call with Noah
            </Hover>
          </div>
        </div>
        <div
          style={css(
            'display:flex;flex-wrap:wrap;gap:14px 40px;align-items:center;justify-content:space-between;margin-top:26px;padding:22px 26px;background:#F6F6F4;border:1px solid #ECEBE6;border-radius:14px'
          )}
        >
          <div>
            <div style={css('font-size:15px;font-weight:700;color:#24276C;margin-bottom:3px')}>
              Need more than Core?
            </div>
            <div style={css('font-size:15px;color:#54545E')}>
              Depletion analysis, custom reporting, 11+ seats, or anything beyond Core.
            </div>
          </div>
          <a href={CALENDAR_URL} style={css('font-size:15px;font-weight:600;color:#24276C')}>
            Book a call with Noah →
          </a>
        </div>
        <p style={css('font-size:14px;color:#8A8A93;margin:18px 0 0')}>
          Month-to-month, no annual commitment. 10 accounts per seat per week applies to any market.
          Utah and California cost the same.
        </p>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:74px 28px')}>
          <div style={css('max-width:720px;margin-bottom:42px')}>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
              )}
            >
              FAQ
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
              )}
            >
              Questions, answered.
            </h2>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:16px 28px'
            )}
          >
            {FAQS.map(([q, a]) => (
              <div
                key={q}
                style={css(
                  'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:24px 24px'
                )}
              >
                <div
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:17px;font-weight:700;color:#24276C;margin-bottom:8px"
                  )}
                >
                  {q}
                </div>
                <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={css('max-width:1180px;margin:0 auto;padding:88px 28px;text-align:center')}>
        <h2
          style={css(
            "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,4vw,46px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 auto 18px;color:#24276C;max-width:720px;text-wrap:balance"
          )}
        >
          Ready to know where your team should hunt next?
        </h2>
        <p
          style={css(
            'font-size:19px;line-height:1.5;color:#54545E;margin:0 auto 34px;max-width:560px'
          )}
        >
          Get started for free in just a few minutes. No credit card or setup call required. Your
          first scored, reasoned account list arrives at the start of the week.
        </p>
        <div style={css('display:flex;flex-wrap:wrap;gap:14px;justify-content:center')}>
          <Hover
            to={START_FREE_URL}
            style={css(
              'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:16px 30px;border-radius:10px'
            )}
            hoverStyle={css('background:#D3341F')}
          >
            Start free
          </Hover>
          <Hover
            href={CALENDAR_URL}
            style={css(
              'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#24276C;background:#fff;border:1.5px solid #D8D6D0;padding:16px 30px;border-radius:10px'
            )}
            hoverStyle={css('border-color:#24276C')}
          >
            Book a call with Noah
          </Hover>
        </div>
      </section>

      <SiteFooter
        navLinks={[
          { label: 'Home', href: '/' },
          { label: 'How it works', href: '#how-it-works' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'FAQ', href: '#faq' },
          { label: 'Contact', href: CONTACT_MAILTO },
        ]}
      />
    </div>
  );
}
